import type { ParsedRow } from './useConvivesActions';

/** Ce que peut devenir une colonne du fichier — ou rien. */
export type ChampCible = 'last_name' | 'first_name' | 'email' | 'ignore';

export const CHAMPS_CIBLES: { value: ChampCible; label: string }[] = [
    { value: 'last_name', label: 'Nom' },
    { value: 'first_name', label: 'Prénom' },
    { value: 'email', label: 'Adresse électronique' },
    { value: 'ignore', label: 'Ignorer cette colonne' },
];

export interface FichierLu {
    fileName: string;
    /** En-têtes tels qu'ils sont dans le fichier (BOM retiré à la comparaison seulement). */
    columns: string[];
    rows: Record<string, any>[];
    /** Association proposée automatiquement, colonne par colonne. */
    mapping: Record<string, ChampCible>;
}

// Détection tolérante des colonnes, même principe que le parseur de référence de l'app
// (src/server/app.ts:376-416, repris ici côté navigateur avec xlsx) — accepte un CSV ou un Excel, avec des
// en-têtes en français ou en anglais. Le ﻿ (BOM) est retiré explicitement : un CSV réenregistré depuis
// un éditeur de texte Windows (Notepad) est souvent sauvé en "UTF-8 avec BOM", qui se colle invisiblement au
// tout premier en-tête ("prénom" étant la première colonne) et l'empêchait de correspondre malgré un texte
// identique à l'œil — bug remonté par Emeni (les prénoms n'apparaissaient jamais dans l'aperçu).
const cleanKey = (k: string) => k.replace(/^﻿/, '').trim().toLowerCase();
const findCol = (keys: string[], ...candidates: string[]) => keys.find((k) => candidates.some((c) => cleanKey(k) === c.toLowerCase())) ?? null;

/**
 * UN CSV N'EST QUE DES OCTETS : c'est à nous de dire dans quel alphabet les lire.
 *
 * xlsx, faute d'indication, lit un CSV en windows-1252 — l'encodage d'un export Excel français. Un
 * fichier produit par Google Sheets, Numbers ou n'importe quel outil moderne est en UTF-8 : ses accents
 * arrivaient alors en « PrÃ©nom », « LÃ©a ». L'en-tête abimé était rattrapé par le repli sur la colonne
 * restante, mais les PRÉNOMS EUX-MÊMES entraient abimés en base, et repartaient tels quels dans
 * l'invitation envoyée au convive. Vu en important un fichier UTF-8 le 18/09.
 *
 * On tranche donc nous-mêmes : un décodage UTF-8 strict qui réussit prouve que le fichier est en UTF-8
 * (une suite d'octets latin-1 accentuée n'est presque jamais de l'UTF-8 valide) ; s'il échoue, on repasse
 * en windows-1252, l'ancien comportement. Un vrai classeur .xlsx porte son encodage en lui : on ne touche
 * pas à sa lecture binaire.
 */
const lireSelonEncodage = (file: File, buf: ArrayBuffer): [any, any] => {
    const estTexte = /\.(csv|txt|tsv)$/i.test(file.name) || /^text\//.test(file.type || '');
    if (!estTexte) return [new Uint8Array(buf), { type: 'array' }];
    try {
        const texte = new TextDecoder('utf-8', { fatal: true }).decode(buf);
        return [texte, { type: 'string' }];
    } catch {
        return [new Uint8Array(buf), { type: 'array' }];
    }
};

/** Lit le fichier dans le navigateur (jamais envoyé brut au serveur) et propose l'association des colonnes. */
export async function lireFichierConvives(file: File): Promise<FichierLu> {
    const buf = await file.arrayBuffer();
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(...lireSelonEncodage(file, buf));
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    if (rows.length === 0) throw new Error('Le fichier est vide.');
    const keys = Object.keys(rows[0]);
    const colEmail = findCol(keys, 'email', 'e-mail', 'mail', 'courriel', 'adresse');
    const colNom = findCol(keys, 'nom', 'last_name', 'lastname', 'name');
    // "prénom" est la colonne la plus fragile à faire correspondre par nom exact : un accent mal
    // encodé selon l'éditeur/le logiciel utilisé pour produire le fichier peut le rendre méconnaissable
    // alors que "nom"/"email" (sans accent) passent sans problème — bug remonté par Emeni malgré un
    // premier correctif (BOM). Repli robuste : s'il ne reste plus qu'UNE seule colonne non réclamée par
    // nom/email, on la prend pour le prénom, quel que soit son libellé exact.
    let colPrenom = findCol(keys, 'prénom', 'prenom', 'first_name', 'firstname');
    if (!colPrenom) {
        const unclaimed = keys.filter((k) => k !== colNom && k !== colEmail);
        if (unclaimed.length === 1) colPrenom = unclaimed[0];
    }
    const mapping: Record<string, ChampCible> = {};
    keys.forEach((k) => {
        mapping[k] = k === colEmail ? 'email' : k === colNom ? 'last_name' : k === colPrenom ? 'first_name' : 'ignore';
    });
    return { fileName: file.name, columns: keys, rows, mapping };
}

/** Applique l'association choisie : une ligne par convive, valeurs nettoyées. */
export function appliquerMapping(lu: FichierLu, mapping: Record<string, ChampCible>): ParsedRow[] {
    const colOf = (champ: ChampCible) => lu.columns.find((k) => mapping[k] === champ) || null;
    const cE = colOf('email'), cN = colOf('last_name'), cP = colOf('first_name');
    return lu.rows.map((r) => ({
        first_name: cP ? String(r[cP] || '').trim() : '',
        last_name: cN ? String(r[cN] || '').trim() : '',
        email: cE ? String(r[cE] || '').trim() : '',
    }));
}

/** Lettre de colonne à la façon d'un tableur : A, B, … Z, AA. */
export const lettreColonne = (i: number): string => {
    let n = i, s = '';
    do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
    return s;
};
